import gql from 'graphql-tag';

import { useRef } from 'react';
import { InlineRedirectFragment } from '@graphqlTypes';
import { ACFMediaFragment } from '@fragments';
import ArrowTailRight from "@/assets/svg/arrow-tail-right-no-padding.svg";
import { Container, Image, RevealWrapper,RhLink, SectionHeader } from '@components';

import { useInView, motion } from "motion/react";
import { AnimVals } from '@/utils/animUtils';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useDebug } from '@/utils/useDebugLog';

import className from 'classnames/bind';
import styles from './InlineRedirect.module.scss';
const cx = className.bind(styles);

// -------------------- Card item

const InlineRedirectCard = (props: InlineRedirectFragment['items'][number] & { index: number, isRevealEnabled: boolean, isInView: boolean }) => {
  const { linkType, externalLink, page, image, body, title, index, isRevealEnabled, isInView } = props;
  const isExternal = linkType[0] === 'external';
  const target = isExternal ? '_blank' : undefined;
  const url = isExternal ? externalLink : page?.node?.uri;

  // -------------------- Animate in

  const ANIM_DIST = AnimVals.sm.distance;
  const ANIM_OPACITY = AnimVals.opacity;
  const ANIM_EASE = AnimVals.ease;
  const ANIM_DUR = AnimVals.sm.duration;
  const ANIM_DELAY = AnimVals.sm.delay + (index * AnimVals.sm.delay);

  const createMotionProps = (delay: number = 0) => {
    return {
      initial: isRevealEnabled ? { opacity: ANIM_OPACITY, y: ANIM_DIST } : {},
      animate: isInView ? { opacity: 1, y: 0 } : {},
      transition: { delay, duration: ANIM_DUR, ease: ANIM_EASE }
    };
  };

  // -------------------- Render

  return (
    <motion.div className={cx("inline-redirect-item")} {...createMotionProps(ANIM_DELAY)}>
      <RhLink href={url} className={cx("inline-redirect-item-content")} target={target}>
        <div className={cx("image", "inline-redirect-item-image")}>
          <Image image={image} />
        </div>
        <div className={cx("content")}>
          <div className={cx("headline")}>
            <div className={cx("image-small")}>
              <Image image={image} />
            </div>
            <p className={cx("title-lc-s", "title")}>{title}</p>
            <span className={cx("arrow")}>
              <ArrowTailRight />
            </span>
          </div>
          <p className={cx("text-m", "inline-redirect-item-body")} dangerouslySetInnerHTML={{ __html: body }} />
        </div>
      </RhLink>
    </motion.div>
  );

}

// -------------------- Main Component

export default function InlineRedirect(props: InlineRedirectFragment) {
  const { log } = useDebug({ name: 'Inline Redirect', bgColor: 'violet', enabled: false });
  const { items, headline, subhead } = props;
  const showHeader = Boolean(headline || subhead);

  // -------------------- Animation

  const rootEl = useRef<HTMLDivElement>(null);
  const { isDesktop } = useViewSize();
  const REVEAL = AnimVals;
  const triggerPercent = isDesktop ? REVEAL.lg.triggerPercent : REVEAL.sm.triggerPercent;
  const isInView = useInView(rootEl, { amount: triggerPercent, once: REVEAL.once });
  const isRevealEnabled = true;

  // -------------------- Render

  return (
    <section className={cx(['inline-redirect'])} ref={rootEl}>
      <Container>
        <RevealWrapper isInView={isInView}>
          { showHeader && (
            <SectionHeader
              headline={headline}
              subhead={subhead}
              isRevealEnabled
              isInView={isInView}
            />
          )}
          <div className={cx("inline-redirect-row")}>
            {items.map((item, index) => (
              <InlineRedirectCard
                key={index}
                {...item}
                index={index}
                isRevealEnabled={isRevealEnabled}
                isInView={isInView}
              />
            ))}
          </div>
        </RevealWrapper>
      </Container>
    </section>
  );

}

InlineRedirect.fragments = {
  entry: gql`
    ${ACFMediaFragment}
    fragment InlineRedirect on FlexiblePageContentContentInlineRedirectLayout {
      headline
      subhead
      items {
        body
        title
        linkType
        externalLink
        page {
          node {
            uri
          }
        }
        image {
          ...ACFMedia
        }
      }
    }
  `,
};
